import React from 'react';
import { Price } from '@noths/polaris-client-ribbons-design-system';
import { priceFormatter } from '@noths/polaris-client-utils';

import type { ProductState } from 'src/redux/product';
import { StarRating } from '../StarRating/StarRating';
import * as styles from './ProductPriceAndRating.styles';

export const CURRENT_PRICE_TEST_ID = 'current-price';

export interface ProductPriceAndRatingProps {
  averageRating?: number;
  numberOfReviews?: number;
  onReviewsLinkClick?: (evt: React.MouseEvent<HTMLAnchorElement>) => void;
  preSalePrices?: ProductState['preSalePrices'];
  price: ProductState['price'];
  priceIsMinimum?: boolean;
  reviewsLinkHref: string;
  salePercentage?: number;
}

export const ProductPriceAndRating = ({
  averageRating,
  numberOfReviews = 0,
  onReviewsLinkClick = () => {},
  preSalePrices,
  price,
  priceIsMinimum = false,
  reviewsLinkHref,
  salePercentage = 0,
}: ProductPriceAndRatingProps) => {
  const { currency } = price;
  const preSalePrice = preSalePrices?.find((preSalePrice) => preSalePrice.currency === currency);
  const priceDiff = preSalePrice ? preSalePrice.amount - price.amount : 0;

  return (
    <section css={styles.root}>
      <div css={styles.prices}>
        {priceIsMinimum && <span css={styles.from}>From</span>}
        <Price
          amount={price.amount}
          css={styles.currentPrice}
          currency={currency}
          testId={CURRENT_PRICE_TEST_ID}
          type={preSalePrice !== undefined ? 'sale' : 'normal'}
        />
        {preSalePrice && (
          <Price
            amount={preSalePrice.amount}
            css={styles.previousPrice}
            currency={currency}
            type="previous"
          />
        )}
      </div>
      {salePercentage > 0 && (
        <div css={styles.saleInfo}>
          <b css={styles.percentageOff}>{salePercentage}% off</b>
          <b css={styles.saveAmount}>Save {priceFormatter(currency, priceDiff)}</b>
        </div>
      )}
      {averageRating && (
        <div css={styles.rating}>
          <a css={styles.reviewsLink} href={reviewsLinkHref} onClick={onReviewsLinkClick}>
            <StarRating productRating={averageRating} />
            <span css={styles.largeNumber}>{averageRating}</span>
            <span css={styles.smallNumber}>/5</span>
            <br />
            {numberOfReviews && (
              <span css={styles.reviewsText}>
                {numberOfReviews} Product {numberOfReviews > 1 ? 'reviews' : 'review'}
              </span>
            )}
          </a>
        </div>
      )}
    </section>
  );
};
